import type { QuickLoanRepaymentScheduleItem } from "../@types";

export function parseScheduleAmount(value: unknown): number {
  const amount = Number(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(amount) && amount >= 0 ? amount : 0;
}

const applied = (value: unknown) =>
  value === true || value === 1 || value === "1" || value === "true";

export function isInstallmentPaid(row: QuickLoanRepaymentScheduleItem): boolean {
  const hasFlags = row.IsPrincipalApplied !== undefined || row.IsInterestApplied !== undefined;
  if (!hasFlags) return row.status === "paid";
  const principalZero = row.Principal != null && parseScheduleAmount(row.Principal) === 0;
  const interestZero = row.Interest != null && parseScheduleAmount(row.Interest) === 0;
  return (principalZero || applied(row.IsPrincipalApplied)) &&
    (interestZero || applied(row.IsInterestApplied)) &&
    (row.Fee == null || parseScheduleAmount(row.Fee) === 0 || applied(row.IsFeeApplied));
}

/** Accept Alert's raw response and our stored canonical schedule. Never guess a missing breakdown. */
export function normalizeRepaymentSchedule(raw: unknown): QuickLoanRepaymentScheduleItem[] {
  if (typeof raw === "string") {
    try { raw = JSON.parse(raw); } catch { return []; }
  }
  if (!Array.isArray(raw)) return [];
  return raw.map((value) => {
    const entry = value && typeof value === "object" ? value : {};
    const row: QuickLoanRepaymentScheduleItem = {
      ...entry,
      Id: entry.Id ?? entry.partner_ref ?? entry.seq,
      PaymentDueDate: entry.PaymentDueDate ?? entry.due_date,
      Total: entry.Total ?? (entry.amount != null ? String(entry.amount) : undefined),
      Principal: entry.Principal ?? (entry.principal != null ? String(entry.principal) : undefined),
      Interest: entry.Interest ?? (entry.interest != null ? String(entry.interest) : undefined),
      Fee: entry.Fee ?? (entry.fee != null ? String(entry.fee) : undefined),
      status: entry.status,
    };
    const paid = isInstallmentPaid(row);
    const partial = !paid && (applied(row.IsPrincipalApplied) || applied(row.IsInterestApplied) ||
      (parseScheduleAmount(row.Fee) > 0 && applied(row.IsFeeApplied)) || entry.status === "partial");
    row.status = paid ? "paid" : partial ? "partial" : entry.status ?? "pending";
    if (paid) row.RemainingTotal = "0";
    else if (entry.remaining_amount !== undefined) {
      row.RemainingTotal = entry.remaining_amount == null ? null : String(entry.remaining_amount);
    } else if (partial) {
      const hasCompleteBreakdown = row.Principal != null && row.Interest != null &&
        Math.abs(parseScheduleAmount(row.Total) - parseScheduleAmount(row.Principal) -
          parseScheduleAmount(row.Interest) - parseScheduleAmount(row.Fee)) < 0.01;
      row.RemainingTotal = hasCompleteBreakdown
        ? String((applied(row.IsPrincipalApplied) ? 0 : parseScheduleAmount(row.Principal)) +
          (applied(row.IsInterestApplied) ? 0 : parseScheduleAmount(row.Interest)) +
          (applied(row.IsFeeApplied) ? 0 : parseScheduleAmount(row.Fee)))
        : null;
    } else row.RemainingTotal = row.Total;
    return row;
  });
}
